/* eslint-disable jsdoc/require-returns */
/* eslint-disable jsdoc/require-param-type */

/**
 * BarChart Component
 *
 * A reusable bar chart component built with Recharts.
 * Supports vertical/horizontal layouts and category-based coloring.
 *
 * @example
 * // Basic vertical bar chart
 * <BarChart
 *   data={[
 *     { name: 'Product A', sales: 100 },
 *     { name: 'Product B', sales: 80 },
 *   ]}
 *   xKey="name"
 *   yKey="sales"
 * />
 *
 * @example
 * // Horizontal bar chart with category colors
 * <BarChart
 *   data={[
 *     { name: 'Amazon', value: 0.38, tier: 'Budget' },
 *     { name: 'Wayfair', value: 0.15, tier: 'Mid-Tier' },
 *   ]}
 *   xKey="name"
 *   yKey="value"
 *   colorKey="tier"
 *   colorMap={{ Budget: '#6366F1', 'Mid-Tier': '#06B6D4' }}
 *   layout="horizontal"
 *   formatY={(v) => `${(v * 100).toFixed(0)}%`}
 * />
 */

import { useMemo } from 'react';

import PropTypes from 'prop-types';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';

import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';

import * as Styled from '../Charts.style';
import { getChartColors, getColorFromMap } from '../utils/colors';
import { identity } from '../utils/formatters';
/**
 * Custom tooltip component
 * @param root0
 * @param root0.active
 * @param root0.payload
 * @param root0.formatTooltip
 * @param root0.formatY
 */
const CustomTooltip = ({ active, payload, formatTooltip, formatY }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0]?.payload;

  if (!item) {
    return null;
  }

  // Use custom formatter if provided
  if (formatTooltip) {
    const tooltipData = formatTooltip(item);

    return (
      <Styled.TooltipContainer>
        {tooltipData.title && (
          <Styled.TooltipTitle size="xs">{tooltipData.title}</Styled.TooltipTitle>
        )}
        {Object.entries(tooltipData)
          .filter(([key]) => key !== 'title' && key !== 'badge')
          .map(([label, value]) => (
            <Styled.TooltipRow key={label}>
              <Text size="xs">{label}:</Text>
              <Text size="xs" weight="medium">
                {value}
              </Text>
            </Styled.TooltipRow>
          ))}
        {tooltipData.badge && (
          <Styled.TooltipBadge color={item.fill} size="xs">
            {tooltipData.badge}
          </Styled.TooltipBadge>
        )}
      </Styled.TooltipContainer>
    );
  }

  // Default tooltip
  return (
    <Styled.TooltipContainer>
      <Styled.TooltipTitle size="xs">{item.name}</Styled.TooltipTitle>
      <Styled.TooltipRow>
        <Text size="xs">Value:</Text>
        <Text size="xs" weight="medium">
          {formatY(payload[0].value)}
        </Text>
      </Styled.TooltipRow>
    </Styled.TooltipContainer>
  );
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  formatTooltip: PropTypes.func,
  formatY: PropTypes.func
};

/**
 * BarChart Component
 * @param root0
 * @param root0.data
 * @param root0.xKey
 * @param root0.yKey
 * @param root0.colorKey
 * @param root0.color
 * @param root0.colorMap
 * @param root0.formatX
 * @param root0.formatY
 * @param root0.formatTooltip
 * @param root0.layout
 * @param root0.height
 * @param root0.showLegend
 * @param root0.showLabels
 * @param root0.showGrid
 * @param root0.barSize
 * @param root0.barRadius
 * @param root0.title
 * @param root0.margin
 */
const BarChart = ({
  // Data
  data = [],
  xKey = 'name',
  yKey = 'value',
  colorKey = null,

  // Colors
  color = null,
  colorMap = {},

  // Formatting
  formatX = identity,
  formatY = identity,
  formatTooltip = null, // eslint-disable-line no-unused-vars

  // Layout
  layout = 'vertical',
  height = 300,
  showLegend = true,
  showLabels = true,
  showGrid = true,
  barSize = null,
  barRadius = 4,
  title = '',

  // Margins
  margin = null
}) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const defaultColor = color || chartColors.primary;
  // Process data with colors
  const chartData = useMemo(
    () =>
      data.map((item) => ({
        ...item,
        name: item[xKey],
        value: item[yKey],
        fill: colorKey ? getColorFromMap(item[colorKey], colorMap, defaultColor) : defaultColor
      })),
    [data, xKey, yKey, colorKey, colorMap, defaultColor]
  );

  // Get unique color categories for legend
  const colorCategories = useMemo(() => {
    if (!colorKey || !showLegend) {
      return [];
    }

    const categories = [...new Set(data.map((item) => item[colorKey]))];

    return categories.map((cat) => ({
      name: cat,
      color: getColorFromMap(cat, colorMap, defaultColor)
    }));
  }, [colorKey, colorMap, defaultColor, data, showLegend]);

  // Empty state
  if (!data?.length) {
    return (
      <Styled.ChartWrapper>
        {title && <Styled.ChartTitle>{title}</Styled.ChartTitle>}
        <Styled.EmptyState>No data available</Styled.EmptyState>
      </Styled.ChartWrapper>
    );
  }

  // Determine if horizontal layout
  const isHorizontal = layout === 'horizontal';

  // Default margins based on layout
  const defaultMargin = isHorizontal
    ? { top: 20, right: 30, left: 100, bottom: 20 }
    : { top: 20, right: 30, left: 40, bottom: 60 };

  const chartMargin = margin || defaultMargin;

  // Custom legend renderer
  const renderLegend = () => {
    if (!showLegend || colorCategories.length === 0) {
      return null;
    }

    return (
      <Styled.LegendContainer>
        {colorCategories.map((cat) => (
          <Styled.LegendItem key={cat.name}>
            <Styled.LegendColor style={{ backgroundColor: cat.color }} />
            <Styled.LegendText>{cat.name}</Styled.LegendText>
          </Styled.LegendItem>
        ))}
      </Styled.LegendContainer>
    );
  };

  return (
    <Styled.ChartWrapper>
      {title && <Styled.ChartTitle>{title}</Styled.ChartTitle>}
      <ResponsiveContainer height={height} width="100%">
        <RechartsBarChart
          data={chartData}
          layout={isHorizontal ? 'vertical' : 'horizontal'}
          margin={chartMargin}
        >
          {showGrid && <CartesianGrid stroke={chartColors.grid} strokeDasharray="3 3" />}

          {isHorizontal ? (
            // Horizontal layout: categories on Y-axis, values on X-axis
            <>
              <XAxis
                stroke={chartColors.axis}
                tick={{ fontSize: 12 }}
                tickFormatter={formatY}
                type="number"
              />
              <YAxis
                dataKey="name"
                stroke={chartColors.axis}
                tick={{ fontSize: 11 }}
                tickFormatter={formatX}
                type="category"
                width={90}
              />
            </>
          ) : (
            // Vertical layout: categories on X-axis, values on Y-axis
            <>
              <XAxis
                dataKey="name"
                height={60}
                interval={0}
                stroke={chartColors.axis}
                tick={{ fontSize: 11, angle: -45, textAnchor: 'end' }}
                tickFormatter={formatX}
              />
              <YAxis stroke={chartColors.axis} tick={{ fontSize: 12 }} tickFormatter={formatY} />
            </>
          )}

          {/* <Tooltip content={<CustomTooltip formatTooltip={formatTooltip} formatY={formatY} />} /> */}

          {showLegend && colorCategories.length > 0 && <Legend content={renderLegend} />}

          <Bar
            dataKey="value"
            maxBarSize={barSize || 60}
            radius={isHorizontal ? [0, barRadius, barRadius, 0] : [barRadius, barRadius, 0, 0]}
          >
            {chartData.map((entry) => (
              <Cell key={entry.name} fill={entry.fill} />
            ))}
            {showLabels && (
              <LabelList
                dataKey="value"
                formatter={formatY}
                position={isHorizontal ? 'right' : 'top'}
                style={{ fontSize: 11, fontWeight: 'bold', fill: chartColors.axis }}
              />
            )}
          </Bar>
        </RechartsBarChart>
      </ResponsiveContainer>
    </Styled.ChartWrapper>
  );
};

BarChart.propTypes = {
  /** Array of data objects */
  data: PropTypes.arrayOf(PropTypes.object),
  /** Key for category names (X-axis in vertical, Y-axis in horizontal) */
  xKey: PropTypes.string,
  /** Key for values */
  yKey: PropTypes.string,
  /** Key for category-based coloring (optional) */
  colorKey: PropTypes.string,
  /** Default bar color (when no colorKey) */
  color: PropTypes.string,
  /** Map of category values to colors */
  colorMap: PropTypes.object,
  /** Formatter for category labels */
  formatX: PropTypes.func,
  /** Formatter for value labels */
  formatY: PropTypes.func,
  /** Custom tooltip formatter: (dataItem) => { title, ...labels, badge } */
  formatTooltip: PropTypes.func,
  /** Chart layout */
  layout: PropTypes.oneOf(['vertical', 'horizontal']),
  /** Chart height in pixels */
  height: PropTypes.number,
  /** Show color legend (only when colorKey is provided) */
  showLegend: PropTypes.bool,
  /** Show value labels on bars */
  showLabels: PropTypes.bool,
  /** Show grid lines */
  showGrid: PropTypes.bool,
  /** Maximum bar width */
  barSize: PropTypes.number,
  /** Bar corner radius */
  barRadius: PropTypes.number,
  /** Chart title */
  title: PropTypes.string,
  /** Chart margins */
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number
  })
};

export default BarChart;
